// covered-by: frontends/carrie/src/routes/ask-puntt/components/try-walkthrough/__tests__/TryWalkthroughDialog.test.tsx
import { CircleNotch } from "@phosphor-icons/react";
import { Flex, Text, TextArea } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type AttachmentItem } from "../../hooks/useAttachedFiles";
import { useFileDrop } from "../../hooks/useFileDrop";
import {
  HomeComposerModeToggle,
  type HomeComposerMode,
} from "../HomeComposerModeToggle";
import { HomeProjectPicker } from "../HomeProjectPicker";
import { InputPlusMenu } from "../InputPlusMenu";

export type TryWalkthroughComposeValue = {
  mode: HomeComposerMode;
  projectId: string | null;
  projectName: string | null;
  sharedChatId?: string | null;
  message: string;
};

type TryWalkthroughComposeProps = {
  value: TryWalkthroughComposeValue;
  onChange: (next: TryWalkthroughComposeValue) => void;
  onNewProject: () => void;
  disabled?: boolean;
  attachedItems?: AttachmentItem[];
  onFilesSelected?: (files: File[]) => void;
  onRemoveFile?: (index: number) => void;
  "data-testid"?: string;
};

/**
 * Last-step composer for the home Chat/Project Try walkthrough. Mirrors the
 * home composer: Chat vs Project, attach/paste/drop, chips, optional project
 * picker. Get started lives on the dialog footer so Skip / Next stay in one
 * place.
 */
export function TryWalkthroughCompose({
  value,
  onChange,
  onNewProject,
  disabled = false,
  attachedItems = [],
  onFilesSelected,
  onRemoveFile,
  "data-testid": dataTestId = "ask-puntt-try-walkthrough-compose",
}: TryWalkthroughComposeProps) {
  const { t } = useTranslation("ui");
  const addFiles = (files: File[]) => {
    if (disabled || files.length === 0) return;
    onFilesSelected?.(files);
  };
  const { isDragOver, dropZoneProps, handlePaste } = useFileDrop({
    onDrop: addFiles,
  });

  return (
    <div className="mt-4" data-testid={dataTestId}>
      <div
        className={`relative overflow-hidden rounded-2xl border bg-base-white transition-colors ${
          isDragOver ? "border-puntt-accent-9" : "border-puntt-neutral-gray-6"
        }`}
        data-testid="ask-puntt-try-walkthrough-compose-card"
        {...dropZoneProps}
      >
        <TextArea
          data-testid="ask-puntt-try-walkthrough-compose-input"
          size="2"
          variant="surface"
          disabled={disabled}
          placeholder={
            value.mode === "project" ? t("home.askInProject") : t("askAnything")
          }
          aria-label={t("tryWalkthrough.home_project.composeInputAriaLabel")}
          value={value.message}
          onChange={(e) => onChange({ ...value, message: e.target.value })}
          onPaste={handlePaste}
          className="w-full resize-none"
          style={{ height: "88px" }}
        />

        {attachedItems.length > 0 && (
          <Flex gap="2" wrap="wrap" className="px-3 pb-1">
            {attachedItems.map((item, idx) => (
              <Flex
                key={item.id}
                align="center"
                gap="1"
                className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                data-testid={`ask-puntt-try-walkthrough-attached-file-${item.id}`}
              >
                {item.status === "pending" && (
                  <CircleNotch
                    size={12}
                    weight="bold"
                    className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                  />
                )}
                <Text
                  size="1"
                  className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                >
                  {item.file.name}
                  {item.status === "error" && " (failed)"}
                </Text>
                <button
                  type="button"
                  data-testid={`ask-puntt-try-walkthrough-attached-file-remove-${item.id}`}
                  disabled={disabled}
                  onClick={() => onRemoveFile?.(idx)}
                  className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={t("home.removeFileAriaLabel", {
                    name: item.file.name,
                  })}
                >
                  ×
                </button>
              </Flex>
            ))}
          </Flex>
        )}

        <Flex align="center" gap="2" className="p-1">
          <InputPlusMenu
            iconSize={16}
            testIdPrefix="ask-puntt-try-walkthrough"
            disabled={disabled}
            onFilesSelected={addFiles}
            onScreenshotCaptured={(file) => addFiles([file])}
          />
          <HomeComposerModeToggle
            data-testid="ask-puntt-try-walkthrough-composer-mode"
            testIdPrefix="ask-puntt-try-walkthrough"
            value={value.mode}
            onChange={(mode) => onChange({ ...value, mode })}
          />
        </Flex>
      </div>

      <div
        className="mt-3 min-h-8"
        data-testid="ask-puntt-try-walkthrough-project-lane"
      >
        {value.mode === "project" ? (
          <HomeProjectPicker
            testIdPrefix="ask-puntt-try-walkthrough"
            variant="inline"
            selectedProjectId={value.projectId}
            selectedProjectName={value.projectName}
            onSelect={(project) =>
              onChange({
                ...value,
                projectId: project.id,
                projectName: project.name,
                sharedChatId: project.sharedChatId,
              })
            }
            onClear={() =>
              onChange({
                ...value,
                projectId: null,
                projectName: null,
                sharedChatId: null,
              })
            }
            onNewProject={onNewProject}
          />
        ) : (
          <div
            className="h-8"
            data-testid="ask-puntt-try-walkthrough-project-lane-spacer"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
